import { StreamActions } from "@hotwired/turbo"

// Custom Turbo Stream action to show a toast notification
// Usage: <%= turbo_stream.toast do %>...<% end %>
// Note: Uses Bootstrap Toast API directly instead of Stimulus controller
// to avoid race conditions with lazy loading
StreamActions.toast = function() {
  const container = document.getElementById("toast-container")
  const template = this.templateContent

  if (!container) {
    console.warn('[turbo-stream:toast] toast-container not found')
    return
  }
  if (!template) {
    console.warn('[turbo-stream:toast] No template content found')
    return
  }

  // Clone and append the toast element
  const fragment = template.cloneNode(true)
  const toastEl = fragment.querySelector('.toast') || fragment.firstElementChild
  container.appendChild(fragment)

  // Get the actual element now in DOM (fragment children move to container)
  const insertedToast = container.lastElementChild

  // Initialize and show Bootstrap Toast directly (no Stimulus needed)
  if (insertedToast && window.bootstrap?.Toast) {
    const delay = parseInt(insertedToast.dataset.toastDelayValue || '5000', 10)
    const toast = new window.bootstrap.Toast(insertedToast, {
      autohide: true,
      delay: delay
    })

    // Remove from DOM after hiding
    insertedToast.addEventListener('hidden.bs.toast', () => {
      insertedToast.remove()
    })

    // Wire up close button if present
    const closeBtn = insertedToast.querySelector('[data-action="toast#close"]')
    if (closeBtn) {
      closeBtn.addEventListener('click', () => toast.hide())
    }

    toast.show()
  }
}

// Custom Turbo Stream action to fire analytics events
// Usage: <%= turbo_stream.analytics_event("Product Added", { sku: "ABC123", price: 29.99 }) %>
// Event names follow Segment naming: "Product Added", "Cart Viewed", etc.
StreamActions.analytics_event = function() {
  const eventName = this.getAttribute("event-name")
  const properties = JSON.parse(this.getAttribute("properties") || "{}")

  // Fire via Analytics service (handles GA4, FB, Bing internally)
  if (window.Analytics && typeof window.Analytics.track === 'function') {
    window.Analytics.track(eventName, properties)
  }
}

// Custom Turbo Stream action to show a Bootstrap modal by ID
// Usage: <%= turbo_stream.bs_modal_show("myModalId") %>
StreamActions.bs_modal_show = function() {
  const modalId = this.getAttribute("modal-id")
  const element = document.getElementById(modalId)

  if (element && window.bootstrap && window.bootstrap.Modal) {
    const modal = window.bootstrap.Modal.getOrCreateInstance(element)
    modal.show()
  }
}

// Initialize Bootstrap components after a Turbo Stream update
// Usage: <%= turbo_stream.init_bootstrap("target_id", component: "Tooltip") %>
// Supported components: Tooltip, Popover, Collapse, Dropdown
StreamActions.init_bootstrap = function() {
  const targetId = this.getAttribute("target")
  const targetElement = targetId ? document.getElementById(targetId) : document.body
  const componentType = this.getAttribute("component")

  if (!targetElement || !window.bootstrap) return

  const initializers = {
    Tooltip: () => {
      targetElement.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
        window.bootstrap.Tooltip.getOrCreateInstance(el)
      })
    },
    Popover: () => {
      targetElement.querySelectorAll('[data-bs-toggle="popover"]').forEach(el => {
        window.bootstrap.Popover.getOrCreateInstance(el)
      })
    },
    Collapse: () => {
      targetElement.querySelectorAll('[data-bs-toggle="collapse"]').forEach(el => {
        window.bootstrap.Collapse.getOrCreateInstance(el)
      })
    },
    Dropdown: () => {
      targetElement.querySelectorAll('[data-bs-toggle="dropdown"]').forEach(el => {
        window.bootstrap.Dropdown.getOrCreateInstance(el)
      })
    }
  }

  if (componentType && initializers[componentType]) {
    initializers[componentType]()
  } else {
    // Initialize all if no specific component requested
    Object.values(initializers).forEach(init => init())
  }
}

// Custom Turbo Stream action to show an element by ID (sets display to block/flex based on parent)
// Usage: <%= turbo_stream.show_element("elementId") %>
StreamActions.show_element = function() {
  const elementId = this.getAttribute("element-id")
  const element = document.getElementById(elementId)

  if (element) {
    element.style.display = ''
  }
}

// Custom Turbo Stream action to hide any open Bootstrap modal
StreamActions.bs_modal_hide = function() {
  // Bootstrap only supports one modal at a time, so find and hide any open modal
  const openModals = document.querySelectorAll('.modal.show')
  openModals.forEach((element) => {
    if (window.bootstrap && window.bootstrap.Modal) {
      const modal = window.bootstrap.Modal.getInstance(element)
      if (modal) {
        modal.hide()
      }
    }
  })
}

// Custom Turbo Stream action to hide modal and clear the Turbo Frame
StreamActions.clear_modal = function() {
  // Skip animation and go straight to cleanup since Turbo is removing the modal
  // Trying to hide with animation causes Bootstrap errors when elements are being removed

  // Immediately clean up modal state
  const backdrop = document.querySelector('.modal-backdrop')
  if (backdrop) {
    backdrop.remove()
  }

  // Restore body to normal state
  document.body.classList.remove('modal-open')
  document.body.style.removeProperty('overflow')
  document.body.style.removeProperty('padding-right')

  // Clear the modal Turbo Frame
  const modalFrame = document.getElementById('modal')
  if (modalFrame) {
    modalFrame.innerHTML = ''
  }
}

